import { Condition, Procedure } from "@medplum/fhirtypes";

import { z } from "zod";

import { Extensions } from "@/fhir/enums/extensions";
import { getCodeableConceptCoding } from "@/utils/medplum";
import { zCoding } from "@/utils/zod";

import { readProcedureNoteSource } from "../utils/procedure-notes";

const zProcedureItem = zCoding().extend({
  billerNotes: z.string(),
  providerNotes: z.string(),
  units: z.number(),
  modifiers: z.array(z.string()),
});

// The picker's form shape. What a session needs before it can be saved depends on what it was
// opened to edit, so that rule lives in the picker's save gate (resolveCanSave).
export const ProceduresPickerForm = z.object({
  procedures: z.array(zProcedureItem),
  conditions: z.array(zCoding()),
});

export const proceduresPickerInitialValues: ProceduresPickerFormType = {
  procedures: [],
  conditions: [],
};

export type ProceduresPickerFormType = z.infer<typeof ProceduresPickerForm>;

const getCoding = (resource: Procedure | Condition) => {
  const coding = getCodeableConceptCoding(resource.code);
  return {
    system: coding?.system ?? "",
    code: coding?.code ?? "",
    display: coding?.display ?? "",
  };
};

export const getProceduresPickerInitialValues = (
  procedures: Procedure[],
  conditions: Condition[],
): ProceduresPickerFormType => {
  return {
    procedures: procedures.map((procedure) => ({
      ...getCoding(procedure),
      billerNotes: readProcedureNoteSource(procedure, Extensions.ProcedureBillerNotes),
      providerNotes: readProcedureNoteSource(procedure, Extensions.ProcedureProviderNotes),
      // Matches the capture default in encounter-charge-items.ts: a Procedure with no units
      // extension yet is billed at 1, so the form has to start there too, not at 0.
      units:
        procedure.extension?.find((extension) => extension.url === Extensions.ProcedureUnits)
          ?.valueInteger ?? 1,
      // A single Procedure can carry more than one modifier, each stored as its own extension.
      modifiers: (procedure.extension ?? [])
        .filter((extension) => extension.url === Extensions.ProcedureModifiers)
        .map((extension) => extension.valueString)
        .filter((modifier): modifier is string => !!modifier),
    })),
    conditions: conditions.map(getCoding),
  };
};
